/**
 * The computer opponents.
 *
 * A bot is handed the same redacted `View` a human socket gets — its own hand,
 * everyone else's chip COUNTS, and nothing about what sits in Avalon. It cannot
 * see where Excalibur is, which is the entire game. That constraint is the point:
 * a bot reading `GameState` directly would play perfectly and be no fun at all,
 * and the redaction it plays behind is the same code path that protects a human.
 *
 * The strategy is deliberately legible rather than optimal. A player should be
 * able to watch a bot take the Green Knight and understand why.
 */

import { isSword, type ChipId } from './chips.js';
import type { Answer, View } from './engine.js';

export type BotMove =
  | { t: 'draw'; region: number }
  | { t: 'play'; chip: ChipId }
  | { t: 'answer'; pendingId: number; answer: Answer };

/**
 * How much a bot wants to be holding each chip at the end of the game.
 *
 * Excalibur and the Cursed Blade are the only two that score; everything else is
 * valued by what it can do for you if you play it. The gap between them is
 * deliberately enormous so that no amount of tactical greed ever talks a bot
 * into handing back the sword.
 */
const KEEP_VALUE: Record<ChipId, number> = {
  excalibur: 1000,
  cursedBlade: -1000,
  squireSword: 55,
  merlin: 95,
  greenKnight: 90,
  arthur: 70,
  gawain: 65,
  percival: 60,
  morgana: 55,
  mordred: 55,
  blackKnight: 50,
  robyn: 50,
  thief: 45,
  lancelot: 42,
  guinevere: 40,
  artisan: 40,
  caelia: 40,
  fisherKing: 38,
  ladyOfTheLake: 36,
  merchant: 35,
  galahad: 35,
  spy: 34,
  uther: 32,
  spirit: 30,
  baroness: 28,
  priest: 24,
  peasant: 18,
};

/** Chips that drag swords around the table. Poison when you already hold one. */
const SWORD_MOVERS: ChipId[] = ['greenKnight', 'merlin', 'arthur', 'gawain', 'guinevere', 'uther'];
/** Chips that move whole hands, which is how a held Excalibur gets lost. */
const HAND_MOVERS: ChipId[] = ['ladyOfTheLake', 'mordred'];

function holds(view: View, chip: ChipId): boolean {
  return view.you.hand.includes(chip);
}

function opponents(view: View) {
  return view.players.filter((p) => p.id !== view.you.id);
}

/** Deterministic jitter, so eight bots in one room don't move in lockstep. */
function wobble(view: View, salt: number): number {
  const base = view.round * 31 + view.you.hand.length * 7 + salt * 13 + view.you.id.length;
  return ((base * 2654435761) >>> 0) / 4294967296;
}

/**
 * The whole of a bot's strategic posture, in one word.
 *
 * `defend` — I hold Excalibur. I want the game over, I want nothing that stirs
 *            the swords, and I want to be the least interesting player at the table.
 * `dump`   — I hold the Cursed Blade. Nothing else matters until it is gone.
 * `hunt`   — I hold neither. Go and take one.
 */
function posture(view: View): 'defend' | 'dump' | 'hunt' {
  if (holds(view, 'cursedBlade') && !holds(view, 'excalibur')) return 'dump';
  if (holds(view, 'excalibur')) return 'defend';
  return 'hunt';
}

// ── the move ────────────────────────────────────────────────────────────────

export function botMove(view: View): BotMove | null {
  if (view.you.pending) {
    return { t: 'answer', pendingId: view.you.pendingId, answer: answerPrompt(view) };
  }
  if (view.phase === 'draw' && view.you.drawsOwed > 0) {
    return { t: 'draw', region: pickDrawRegion(view) };
  }
  if (view.phase === 'play' && !view.you.played) {
    const chip = pickPlay(view);
    if (chip) return { t: 'play', chip };
  }
  return null;
}

/**
 * Which region to draw from.
 *
 * The end trigger is an EMPTY region, so region choice is the one lever every
 * player has on the clock. Holding Excalibur means wanting the game over, which
 * means draining the thinnest region. Hunting means wanting more rounds to find
 * it, which means feeding off the fattest.
 */
function pickDrawRegion(view: View): number {
  const live = [0, 1, 2].filter((i) => view.regions[i]! > 0);
  if (live.length === 0) return 0;
  const want = posture(view);
  const sorted = live.slice().sort((a, b) => view.regions[a]! - view.regions[b]!);
  if (want === 'defend') return sorted[0]!;
  return sorted[sorted.length - 1]!;
}

function pickPlay(view: View): ChipId | null {
  const playable = view.you.hand.filter((c) => !isSword(c));
  if (playable.length === 0) return null;
  const want = posture(view);

  const scored = playable.map((chip) => {
    // Base: playing a chip spends it, so a chip you would rather keep costs more
    // to play. Inverted, because the best play is usually the cheapest chip that
    // still does the job.
    let score = 100 - KEEP_VALUE[chip];

    if (want === 'defend') {
      // Anything that stirs the swords can only lose me the one I am holding.
      if (SWORD_MOVERS.includes(chip)) score -= 220;
      if (HAND_MOVERS.includes(chip)) score -= 260;
      if (chip === 'spirit') score -= 90;
      // Quiet, useful, and it burns through Avalon towards the end trigger.
      if (chip === 'peasant' || chip === 'priest' || chip === 'baroness') score += 60;
      if (chip === 'galahad') score += 130;
      if (chip === 'fisherKing') score += 110;
    }

    if (want === 'dump') {
      // Everything here is about getting the curse out of my hand.
      if (chip === 'artisan') score += 200;
      if (chip === 'merchant' || chip === 'peasant') score += 170;
      if (chip === 'ladyOfTheLake') score += 150;
      if (chip === 'uther') score += 140;
      if (chip === 'greenKnight' || chip === 'merlin') score -= 200;
    }

    if (want === 'hunt') {
      if (SWORD_MOVERS.includes(chip)) score += 150;
      if (chip === 'greenKnight' || chip === 'merlin') score += 90;
      if (chip === 'percival' || chip === 'mordred') score += 70;
      if (chip === 'thief' || chip === 'robyn') score += 55;
      // The endgame is close and I have nothing. Swing hard.
      if (view.finalRound) {
        if (chip === 'greenKnight' || chip === 'merlin' || chip === 'arthur') score += 260;
      }
    }

    return { chip, score: score + wobble(view, playable.indexOf(chip)) * 18 };
  });

  scored.sort((a, b) => b.score - a.score);
  return scored[0]!.chip;
}

/**
 * Rank an opponent by how much a bot wants to point at them.
 *
 * With hands hidden, the only real signal is size: a sword is likelier to be in
 * a hand of three than a hand of seven, so the small hands are the concentrated
 * bets. A player already flagged as sword-bearing by a public effect would be
 * better still, but nothing in the view carries that, and inventing it would be
 * the bot cheating.
 */
function rankTargets(view: View): string[] {
  return opponents(view)
    .filter((p) => p.handCount > 0)
    .slice()
    .sort((a, b) => a.handCount - b.handCount || a.id.localeCompare(b.id))
    .map((p) => p.id);
}

function worstToLose(hand: ChipId[]): ChipId {
  return hand.slice().sort((a, b) => KEEP_VALUE[a] - KEEP_VALUE[b])[0]!;
}

function bestToTake(chips: ChipId[]): number {
  let best = 0;
  for (let i = 1; i < chips.length; i++) {
    if (KEEP_VALUE[chips[i]!] > KEEP_VALUE[chips[best]!]) best = i;
  }
  return best;
}

function answerPrompt(view: View): Answer {
  const p = view.you.pending!;
  const want = posture(view);

  switch (p.kind) {
    case 'region': {
      if (!p.allowEmpty) return { kind: 'region', region: pickDrawRegion(view) };
      // Placing something back. Burying a chip in the thinnest region delays the
      // end; burying it in the fattest keeps the thin one on track to empty.
      const order = [0, 1, 2].sort((a, b) => view.regions[a]! - view.regions[b]!);
      return { kind: 'region', region: want === 'defend' ? order[2]! : order[0]! };
    }

    case 'mode': {
      const canSteal = p.modes.some((m) => m.id === 'steal');
      const anyoneWorthRobbing = opponents(view).some((o) => o.handCount > 0);
      if (canSteal && anyoneWorthRobbing && want !== 'defend') return { kind: 'mode', mode: 'steal' };
      const draw = p.modes.find((m) => m.id === 'draw');
      return { kind: 'mode', mode: draw ? 'draw' : p.modes[0]!.id };
    }

    case 'players': {
      if (p.chip === 'caelia') {
        // Caelia burns male chips out of hands. Aim at the biggest hands, which
        // is where the most will burn — and never at myself.
        const fat = opponents(view)
          .filter((o) => p.candidates.includes(o.id))
          .sort((a, b) => b.handCount - a.handCount)
          .slice(0, p.max)
          .map((o) => o.id);
        return { kind: 'players', players: fat.slice(0, Math.max(p.min, Math.min(p.max, fat.length))) };
      }
      const ranked = rankTargets(view).filter((id) => p.candidates.includes(id));
      const pool = ranked.length ? ranked : p.candidates;
      const take = Math.max(p.min, Math.min(p.max, 1));
      return { kind: 'players', players: pool.slice(0, take) };
    }

    case 'hand': {
      // Give away the least useful thing I hold, and take the chance to shed the
      // curse if this prompt will accept it.
      const pool = p.eligible.slice();
      const chips: ChipId[] = [];
      for (let i = 0; i < p.max && pool.length > 0; i++) {
        if (i >= p.min && chips.length >= p.min) break;
        const cursed = pool.indexOf('cursedBlade');
        const idx = cursed >= 0 ? cursed : pool.indexOf(worstToLose(pool));
        chips.push(pool.splice(idx, 1)[0]!);
      }
      // Galahad is the opposite question: which one chip do I shield?
      if (p.chip === 'galahad') {
        const keep = p.eligible.slice().sort((a, b) => KEEP_VALUE[b] - KEEP_VALUE[a])[0]!;
        return { kind: 'hand', chips: [keep] };
      }
      return { kind: 'hand', chips };
    }

    case 'direction': {
      // Uther and Spirit both move things between neighbours. Pull from whichever
      // side holds the tighter hand; 'left' takes from the seat before me.
      const order = view.order.length ? view.order : view.players.map((x) => x.id);
      const i = order.indexOf(view.you.id);
      const before = view.players.find((x) => x.id === order[(i - 1 + order.length) % order.length]);
      const after = view.players.find((x) => x.id === order[(i + 1) % order.length]);
      if (p.chip === 'uther' && want === 'dump') {
        // Any direction sends my curse away; pick the one that also brings me the
        // fuller neighbour's swords.
        return { kind: 'direction', direction: 'left' };
      }
      const takeFromBefore = (before?.handCount ?? 9) <= (after?.handCount ?? 9);
      return { kind: 'direction', direction: takeFromBefore ? 'left' : 'right' };
    }

    case 'pick': {
      // Percival's four, or Morgana's two. Excalibur if it is there, obviously.
      const idx = bestToTake(p.chips);
      if (p.optional) {
        // Morgana: only cast something clearly worth casting.
        const best = p.chips[idx]!;
        const worth = KEEP_VALUE[best] >= 40 || want === 'hunt';
        return { kind: 'pick', index: worth ? idx : null };
      }
      return { kind: 'pick', index: idx };
    }

    case 'confirm': {
      if (p.chip === 'fisherKing') {
        // A coin flip that either makes me untouchable or costs me the chip.
        // Worth it exactly when I have something to lose.
        return { kind: 'confirm', yes: want === 'defend' || holds(view, 'squireSword') };
      }
      return { kind: 'confirm', yes: want !== 'defend' };
    }

    case 'peek': {
      if (p.mode === 'swap') {
        // Mordred. Take their hand only if it is plainly better than mine.
        const theirs = p.peek.reduce((n, c) => n + KEEP_VALUE[c], 0);
        const mine = view.you.hand.reduce((n, c) => n + KEEP_VALUE[c], 0);
        return { kind: 'peek', index: null, swap: theirs > mine + 40 };
      }
      // Spy. Show a sword if there is one — the table turning on them is worth
      // more than the secret.
      const sword = p.peek.findIndex(isSword);
      return { kind: 'peek', index: sword >= 0 ? sword : bestToTake(p.peek) };
    }

    case 'sword': {
      // Forced to give one up. Shed the curse first, then the squire, and only
      // ever surrender Excalibur when it is the only thing left.
      const order: ChipId[] = ['cursedBlade', 'squireSword', 'excalibur'];
      for (const chip of order) if (p.swords.includes(chip)) return { kind: 'sword', chip };
      return { kind: 'sword', chip: p.swords[0]! };
    }

    case 'played': {
      const best = p.options.reduce((a, b) => (KEEP_VALUE[b.chip] > KEEP_VALUE[a.chip] ? b : a));
      return { kind: 'played', ownerId: best.ownerId, chip: best.chip };
    }
  }
}
